<script lang="ts">
  import { LinearToneMapping, MeshBasicMaterial } from 'three';
  import { T, useThrelte } from '@threlte/core';
  import { createTransition, Suspense, transitions } from '@threlte/extras';
  import { cubicOut, sineIn } from 'svelte/easing';
  import type { SceneAssets, SceneParameters } from './types';
  import { getSharedMaterials } from './helpers/material_helpers.svelte';
  import X2, { preload as preloadX2 } from './models/X2.svelte';
  import S3, { preload as preloadS3 } from './models/S3.svelte';
  import T2, { preload as preloadT2 } from './models/T2.svelte';
  import T3, { preload as preloadT3 } from './models/T3.svelte';
  import Laptop, { preload as preloadLaptop } from './models/Laptop.svelte';
  import Phone, { preload as preloadPhone } from './models/Phone.svelte';
  import SquareCard, { preload as preloadSquareCard } from './models/SquareCard.svelte';
  import ReflectivePlane from './models/ReflectivePlane.svelte';

  export let section: number = 0;
  export let sectionPercentage: number = 0;
  export let totalProgress: number = 0;
  export let isMobile: boolean = false;
  export let isLandscape: boolean = false;
  export let scene: SceneAssets;
  export let params: SceneParameters;
  export let pixelRatio: number = 1.5;
  export let forceFallback: () => void;

  const preloadModels = () => {
    if (scene.sections.includes('register')) {
      preloadX2(scene.models.register);
    }
    if (scene.sections.includes('stand')) {
      preloadS3(scene.models.stand);
    }
    if (scene.sections.includes('terminal')) {
      preloadT2(scene.models.terminal);
    }
    if (scene.sections.includes('handheld')) {
      preloadT3(scene.models.handheld);
    }
    if (scene.sections.includes('software')) {
      preloadLaptop(scene.models.laptop);
      preloadPhone(scene.models.phone);
    }
    if (scene.sections.includes('banking')) {
      preloadSquareCard(scene.models.card);
    }
  };

  preloadModels();

  const { renderer, size } = useThrelte();
  let materialMapping = {
    phone: undefined,
    card: undefined,
    laptop: undefined,
    x2: undefined,
    s3: undefined,
    t2: undefined,
    t3: undefined,
  } as any;
  // eslint-disable-next-line unicorn/prefer-top-level-await
  getSharedMaterials(scene.textures, scene.sections).then((mats) => {
    if (mats) {
      materialMapping = mats;
    } else {
      forceFallback();
    }
  });
  $: ({
    phone: phoneMaterial,
    card: cardMaterial,
    laptop: laptopMaterial,
    x2: x2Material,
    s3: s3Material,
    t2: t2Material,
    t3: t3Material,
  } = materialMapping);

  const fallbackMaterial = new MeshBasicMaterial({ color: '#fbf6ef' });
  $: activeSection = scene.sections[Math.max(section - 1, 0)];
  let x2: any;
  let s3: any;
  let t2: any;
  let t3: any;
  let laptop: any;
  let phone: any;
  let cardFront: any;
  let cardBack: any;
  let camera: any;
  let cameraGroup: any;
  // eslint-disable-next-line svelte/no-immutable-reactive-statements
  $: cameraPosition = { x: 0, y: 0 };
  const cameraFovSettings = {
    min: 35,
    max: 60,
    growthRate: 1.75,
  };

  const getCameraFOV = (size: { width: number; height: number }) =>
    Math.min(
      Math.max(
        cameraFovSettings.min,
        2 * Math.atan(cameraFovSettings.growthRate / (size.width / size.height) / (2 * 2)) * (180 / Math.PI)
      ),
      cameraFovSettings.max
    );

  // Set pixel ratio based on device pixel ratio
  pixelRatio = Math.max(window.devicePixelRatio, pixelRatio);
  $: renderer.setPixelRatio(pixelRatio);
  renderer.toneMapping = LinearToneMapping;

  // Animations
  transitions();

  const flyIn = createTransition((ref: any) => ({
    tick(t) {
      // t is [0,1] and needs to be converted to [1,0]
      t = 1 - t;
      ref.position.y = t / -10;
    },
    easing: cubicOut,
    duration: params.transitionDuration,
  }));

  type Vector = [x: number, y: number, z: number];

  $: cameraFov = getCameraFOV($size);
  // This handles the initial camera zoom out, then returns to static
  $: camPos = (
    section === 0
      ? [cameraPosition.x, cameraPosition.y + 0.5 + sineIn(1 - sectionPercentage) * 0.25 * (isMobile ? 0.175 : 1), 0]
      : [cameraPosition.x, cameraPosition.y + 0.5, 0]
  ) as Vector;

  // eslint-disable-next-line svelte/no-reactive-functions
  $: getT2Position = (): Vector => {
    const yOffset = 0.3;
    if (section === 0) {
      return [-1.15, (1 - sectionPercentage) * -0.25 + yOffset, -3.25];
    } else if (section === 1) {
      return [-1.15, yOffset, -3.25];
    }
    return [0, 0.4, -1.75];
  };

  $: T2Pos = getT2Position();
  $: X2Rotation = sectionPercentage * ((Math.PI * 3) / 4) * -1;

  $: t3RotSpeed = params.t3LongAnim ? 2.5 : 1.3 + params.t3AnimOffset * 0.5; // 2.75 for opposite rotation
  $: t3RotDirection = params.t3LongAnim ? -1 : 1;
  $: T3Rotation =
    totalProgress - params.t3AnimOffset > 0
      ? [
          0,
          (totalProgress - params.t3AnimOffset) * (t3RotDirection * t3RotSpeed),
          (sineIn(Math.sin((Math.PI * (totalProgress - params.t3AnimOffset)) / 4) + 0 / -2) * Math.PI) / -10,
        ]
      : [0, 0, 0];
  $: S3Rotation =
    section > 1
      ? (sectionPercentage * ((Math.PI * 1.25) / 2 + 0.4) + 4.8) * -1 * (params.reduceMotion ? 0.25 : 1)
      : ((sectionPercentage + section) * (Math.PI / 2) + 4.65) * -1;
  $: T2Rotation = ((1 - sectionPercentage) * (-Math.PI / 2) + Number(params.reduceMotion ? 0.25 : 1)) * -1;
  $: LaptopPos = [-0.3, isMobile ? 0 : 0.2, -4.2] as Vector;
  $: LaptopRotation = [0, (sectionPercentage * Math.PI) / -10 - Math.PI / 8, 0] as Vector;
  $: PhonePos = [0.44, isMobile ? 0 : 0, -2.8] as Vector;
  $: PhoneRotation = [0, sectionPercentage * (Math.PI / 6) + Math.PI / 12, 0] as Vector;
  $: CreditCardPos = [-0.1, isMobile ? -0.2 : 0, -2.5] as Vector;
  $: CreditCardRotation = [0, (Math.PI / 1.75) * -sectionPercentage + Math.PI / 4, 0] as Vector;
</script>

<!-- Lighting + Contact Shadows -->

<T.Group bind:ref={cameraGroup}>
  <T.PerspectiveCamera makeDefault position={camPos} fov={cameraFov} focus={1} bind:ref={camera} />
</T.Group>

<!-- Visible scene -->
{#if activeSection === 'handheld'}
  <T.Group in={flyIn}>
    <T.Group position={[0, 0.4, -3]} rotation={[T3Rotation[0], T3Rotation[1], T3Rotation[2]]}>
      <T3 bind:ref={t3} matcaps={t3Material} {fallbackMaterial} />
    </T.Group>
  </T.Group>
{/if}

{#if activeSection === 'register'}
  <T.Group in={flyIn}>
    <T.Group position={[0, 0, -3]} rotation.y={X2Rotation}>
      <X2 bind:ref={x2} matcaps={x2Material} {fallbackMaterial} />

      <ReflectivePlane
        percent={sectionPercentage}
        GroupRotation={X2Rotation - 300}
        position={[0, 0.0667, -0.092]}
        rotation.x={-Math.PI / 2 - 0.09}
        rotation.z={Math.PI}
        args={[0.695, 0.317]}
        textures={{
          reflection: scene.textures.register.x2ReflectionBlur2,
          alpha: scene.textures.shared.reflectionAlpha,
        }}
        type="x2"
        {isMobile}
        opacity={0.8}
      />
    </T.Group>
  </T.Group>
{/if}
{#if activeSection === 'terminal'}
  <T.Group in={flyIn}>
    <T.Group position={T2Pos} rotation.y={T2Rotation}>
      <T2 bind:ref={t2} rotation.x={Math.PI / 4} matcaps={t2Material} {fallbackMaterial} />
    </T.Group>
  </T.Group>
{/if}
{#if activeSection === 'stand'}
  <T.Group in={flyIn}>
    <T.Group rotation.y={S3Rotation} position={[0, 0, -3]}>
      <S3 bind:ref={s3} matcaps={s3Material} {fallbackMaterial} />
      <ReflectivePlane
        percent={sectionPercentage}
        GroupRotation={S3Rotation}
        position={[0, 0.047, 0.02]}
        rotation.x={-Math.PI / 2}
        args={[0.54, 0.49]}
        textures={{
          reflection: scene.textures.stand.s3ReflectionBlur,
          alpha: scene.textures.shared.reflectionAlpha,
        }}
        {isMobile}
        {section}
        opacity={0.5}
      />
    </T.Group>
  </T.Group>
{/if}
{#if activeSection === 'software'}
  <T.Group in={flyIn}>
    <T.Group rotation={LaptopRotation} position={LaptopPos}>
      <Suspense final>
        <Laptop bind:ref={laptop} rotation.x={Math.PI / 8} matcaps={laptopMaterial} scale={5} {fallbackMaterial} />
      </Suspense>
    </T.Group>
    <Suspense final>
      <Phone bind:ref={phone} position={PhonePos} rotation={PhoneRotation} matcaps={phoneMaterial} {fallbackMaterial} />
    </Suspense>
  </T.Group>
{/if}
{#if activeSection === 'banking'}
  <T.Group in={flyIn}>
    <T.Group position={CreditCardPos} rotation={CreditCardRotation}>
      <T.Group rotation.x={-Math.PI / 12}>
        <Suspense final>
          <SquareCard
            bind:ref={cardFront}
            position={[0, 0.6, sectionPercentage * 0.02 + 0.1]}
            scale={10}
            matcaps={cardMaterial}
            {fallbackMaterial}
          />
          <ReflectivePlane
            percent={sectionPercentage}
            GroupRotation={1}
            position={[0.23, 0.514, sectionPercentage * -0.03 + 0.025]}
            args={[0.9, 0.5]}
            textures={{
              reflection: scene.textures.card.cardShadow,
            }}
            type="card"
            opacity={0.1 * (Math.sin(2 * Math.PI * (1 - sectionPercentage)) + 1) + 0.05}
            {isMobile}
            renderOrder={2}
          />
          <SquareCard
            bind:ref={cardBack}
            position={[0.19, 0.5, sectionPercentage * -0.03]}
            rotation.y={Math.PI}
            scale={10}
            matcaps={cardMaterial}
            {fallbackMaterial}
          />
        </Suspense>
      </T.Group>
    </T.Group>
  </T.Group>
{/if}
